import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type FormEvent, useState } from "react";
import { jitUserProvisioningEnabled } from "../../../../shared/jitProvisioning";
import { sharedMutators } from "../../../../shared/mutators";
import { ssoIssuerUrlError } from "../../../../shared/ssoIssuerUrl";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
import { Modal } from "../../Modal";
import { SettingsModalBody, SettingsModalFooter } from "../../SettingsLayout";
import { TabLinkButton } from "../../TabButton";
import { Text } from "../../Text";
import { useCurrentUrl, useMakeUrl, routes } from "../../../lib/urls";
import { DangerSection } from "../DangerSection";
import { settingsModalStyle } from "../shared";
import { emptyMapping, emptySsoConfig, parseScopes } from "./constants";
import { newScimToken } from "./scimToken";
import { PrimaryChangeModal } from "./SsoProviderChanges";
import type { SyncMode } from "./SyncModeFields";
import {
  identityProvider,
  identityProviderForIssuer,
  type MetadataSource,
} from "./identityProviders";
import { GuideToggle, SsoProviderSteps } from "./SsoProviderSteps";
import { SsoUsageTab } from "./SsoUsageTab";
import { PROTOCOL_LABELS, SsoModalForm, SsoModalHeader } from "./SsoModal";

const SettingsTab = styled("div")`
  display: contents;
  &[hidden] {
    display: none;
  }
`;

export function ExistingSsoProvider({
  provider,
  onGeneratedScimToken,
  onDelete,
  onClose,
}: {
  provider: {
    id: string;
    providerId: string;
    issuer: string;
    domain: string;
    protocol: string;
    isPrimary: boolean | null;
    syncMode?: string | null;
    groupMappings?: unknown;
    scimTokenHash?: string | null;
    scimTokenExpiresAt?: number | null;
  };
  onGeneratedScimToken: (token: string | null) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const z = useZero();
  const idp = identityProviderForIssuer(provider.issuer);
  // A provider can use a protocol its IdP's template doesn't guide, e.g.
  // Google over OIDC, so fall back to the generic steps for it.
  const setupIdp = idp.protocols[provider.protocol === "saml" ? "saml" : "oidc"]
    ? idp
    : identityProvider("other");
  const [showGuide, setShowGuide] = useState(false);
  const makeUrl = useMakeUrl();
  const { tab } = useCurrentUrl(routes.settings.sso);
  const activeTab = tab === "usage" ? "usage" : "settings";
  const [metadataSource, setMetadataSource] =
    useState<MetadataSource>("metadata");

  const providerSyncMode: SyncMode =
    (provider.syncMode as SyncMode | null | undefined) ?? "off";
  const providerIsPrimary = provider.isPrimary ?? false;

  const [config, setConfig] = useState({
    ...emptySsoConfig,
    protocol:
      provider.protocol === "saml" ? ("saml" as const) : ("oidc" as const),
    providerId: provider.providerId,
    issuer: provider.issuer,
    domain: provider.domain,
    syncMode: providerSyncMode,
    isPrimary: providerIsPrimary,
  });
  const [baseline, setBaseline] = useState(config);
  const [previousPrimary, setPreviousPrimary] = useState(providerIsPrimary);
  if (providerIsPrimary !== previousPrimary) {
    setPreviousPrimary(providerIsPrimary);
    setBaseline({ ...baseline, isPrimary: providerIsPrimary });
    if (config.isPrimary === baseline.isPrimary) {
      setConfig({ ...config, isPrimary: providerIsPrimary });
    }
  }
  const hasChanges = JSON.stringify(config) !== JSON.stringify(baseline);
  const primaryChanged = config.isPrimary !== baseline.isPrimary;
  const [confirmingPrimary, setConfirmingPrimary] = useState(false);
  const issuerInvalid =
    config.protocol === "oidc" &&
    !!config.issuer.trim() &&
    ssoIssuerUrlError(config.issuer.trim()) !== null;

  async function save() {
    const switchingToScim =
      config.syncMode === "scim" && providerSyncMode !== "scim";
    const switchingFromScim =
      providerSyncMode === "scim" && config.syncMode !== "scim";

    // Prepare the token before any mutation so token generation failures cannot leave partial changes.
    const scim = switchingToScim ? await newScimToken() : null;

    if (switchingFromScim) {
      z.mutate(sharedMutators.revokeScimToken({ ssoProviderId: provider.id }));
    }

    z.mutate(
      sharedMutators.updateSSOProvider({
        id: provider.id,
        ...(primaryChanged && { isPrimary: config.isPrimary }),
        ...(config.issuer.trim() &&
          config.issuer.trim() !== provider.issuer && {
            issuer: config.issuer.trim(),
          }),
        ...(config.domain !== provider.domain && {
          domain: config.domain.trim().toLowerCase(),
        }),
        // Switching to scim is handled by generateScimToken below, which sets
        // syncMode together with the token hash.
        ...(config.syncMode !== providerSyncMode &&
          !switchingToScim && {
            syncMode: config.syncMode,
          }),
        ...(config.protocol === "saml"
          ? {
              ...(config.metadataUrl.trim() && {
                metadataUrl: config.metadataUrl.trim(),
              }),
              ...(config.metadataXml.trim() && {
                metadataXml: config.metadataXml.trim(),
              }),
              ...(config.entryPoint && {
                entryPoint: config.entryPoint,
              }),
              ...(config.cert && { cert: config.cert }),
              ...(jitUserProvisioningEnabled({ syncMode: config.syncMode }) &&
                (() => {
                  const { image, ...required } = config.mapping;
                  if (!Object.values(required).every((v) => v.trim()))
                    return {};
                  return {
                    mapping: {
                      email: required.email.trim(),
                      firstName: required.firstName.trim(),
                      lastName: required.lastName.trim(),
                      ...(image.trim() && { image: image.trim() }),
                    },
                  };
                })()),
            }
          : {
              ...(config.clientId && { clientId: config.clientId }),
              ...(config.clientSecret && {
                clientSecret: config.clientSecret,
              }),
              ...(config.scopes.trim() && {
                scopes: parseScopes(config.scopes),
              }),
            }),
      })
    );

    if (scim) {
      z.mutate(
        sharedMutators.generateScimToken({
          ssoProviderId: provider.id,
          tokenHash: scim.hash,
        })
      );
      onGeneratedScimToken(scim.token);
    } else if (switchingFromScim) {
      onGeneratedScimToken(null);
    }

    const resetConfig = {
      ...config,
      clientId: "",
      clientSecret: "",
      scopes: "",
      entryPoint: "",
      cert: "",
      metadataUrl: "",
      metadataXml: "",
      mapping: { ...emptyMapping },
      replaceMapping: false,
    };
    setConfig(resetConfig);
    setBaseline(resetConfig);
  }

  function chooseMetadataSource(next: MetadataSource) {
    setMetadataSource(next);
    setConfig((f) => ({
      ...f,
      metadataUrl: "",
      metadataXml: "",
      entryPoint: "",
      cert: "",
    }));
  }

  function handleSave(e: FormEvent) {
    e.preventDefault();
    if (primaryChanged && !(config.isPrimary && config.primaryReplaceConfirmed))
      setConfirmingPrimary(true);
    else void save();
  }

  const protocolLabel = PROTOCOL_LABELS[config.protocol];

  return (
    <Modal open onClose={onClose} style={settingsModalStyle}>
      <SsoModalForm onSubmit={handleSave}>
        <SsoModalHeader
          idp={idp}
          title={provider.providerId}
          subtitle={idp.logo ? `${idp.name} · ${protocolLabel}` : protocolLabel}
          onClose={onClose}
          tabs={(
            [
              { key: "settings", label: "Settings", icon: "settings-2" },
              { key: "usage", label: "Usage", icon: "chart-no-axes-column" },
            ] as const
          ).map((t) => (
            <TabLinkButton
              key={t.key}
              to={makeUrl(routes.settings.sso, {
                provider: provider.id,
                tab: t.key,
              })}
              className={activeTab === t.key ? "active" : undefined}
            >
              <Icon name={t.icon} size={16} />
              <Text variant="text-small-md">{t.label}</Text>
            </TabLinkButton>
          ))}
          actions={
            activeTab === "settings" && (
              <GuideToggle
                isOn={showGuide}
                onToggle={() => setShowGuide((was) => !was)}
              />
            )
          }
        />
        <SettingsModalBody style={{ gap: 24 }} data-popover-host>
          {activeTab === "usage" && (
            <SsoUsageTab
              providerId={provider.providerId}
              isPrimary={providerIsPrimary}
            />
          )}
          {/* Hidden rather than unmounted so field state survives a visit to the usage tab. */}
          <SettingsTab hidden={activeTab === "usage"}>
            <SsoProviderSteps
              idp={setupIdp}
              config={config}
              onConfigChange={setConfig}
              metadataSource={metadataSource}
              onMetadataSourceChange={chooseMetadataSource}
              showGuide={showGuide}
              existing
              scimToken={
                providerSyncMode === "scim"
                  ? {
                      providerId: provider.id,
                      expiresAt: provider.scimTokenExpiresAt,
                      onRevoked: () => {
                        setConfig({ ...config, syncMode: "off" });
                        setBaseline({ ...baseline, syncMode: "off" });
                      },
                    }
                  : undefined
              }
            />
            <DangerSection
              title="Delete provider"
              description="People can no longer sign in with this provider. Published routes that use it must switch providers first."
              actionLabel="Delete provider"
              onAction={onDelete}
            />
          </SettingsTab>
        </SettingsModalBody>
        <SettingsModalFooter>
          <Button
            variant="primary"
            size="l"
            type="submit"
            disabled={!hasChanges || issuerInvalid}
          >
            Save changes
          </Button>
        </SettingsModalFooter>
      </SsoModalForm>
      {confirmingPrimary && (
        <PrimaryChangeModal
          providerId={provider.providerId}
          isPrimary={config.isPrimary}
          confirmLabel="Save changes"
          onCancel={() => setConfirmingPrimary(false)}
          onConfirm={() => {
            setConfirmingPrimary(false);
            void save();
          }}
        />
      )}
    </Modal>
  );
}
